import type React from 'react';
import type { CSSProperties } from 'react';
import { useTheme } from '@contexts/ThemeContext';

const GREEN_STATUSES = new Set(['npe', 'sppb', 'e', 'export']);
const RED_STATUSES = new Set(['npd', 'sptnp', 'reject', 'billing sptnp']);
const PURPLE_STATUSES = new Set(['i', 'import']);

export const withAlpha = (color: string, alpha: number): string => {
  const hex = color.replace('#', '').slice(0, 6);
  if (hex.length !== 6) {
    return color;
  }

  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Re-hues a palette colour, holding its perceived brightness steady.
 *
 * Used to split one theme token into two categorical accents that stay in the
 * theme's own key. Picking a second token instead does not work here: `blue`,
 * `info` and `accent` are the same value in several palettes, and `purple` is a
 * plain grey in others.
 *
 * It matches relative luminance rather than HSL lightness, which is the whole
 * point: cyan at blue's lightness is far brighter than blue, and reads at about
 * 2:1 on a white row where the blue reads at 4.7:1. Solving for the lightness
 * that reproduces the source's luminance gives both accents the same contrast
 * against whatever the row behind them is, in light themes and dark ones alike,
 * leaving hue as the only thing that differs.
 */
const relativeLuminance = (r: number, g: number, b: number): number => {
  const lin = (v: number) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const seg: [number, number, number] =
    h < 60 ? [c, x, 0]
    : h < 120 ? [x, c, 0]
    : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c]
    : h < 300 ? [x, 0, c]
    : [c, 0, x];
  return [seg[0] + m, seg[1] + m, seg[2] + m];
};

const hueCache = new Map<string, string>();

const withHue = (color: string, hue: number): string => {
  const key = `${color}|${hue}`;
  const cached = hueCache.get(key);
  if (cached) return cached;

  const hex = color.replace('#', '').slice(0, 6);
  if (hex.length !== 6) return color;

  const r = Number.parseInt(hex.slice(0, 2), 16) / 255;
  const g = Number.parseInt(hex.slice(2, 4), 16) / 255;
  const b = Number.parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l0 = (max + min) / 2;
  const d = max - min;
  const sat = d === 0 || l0 === 0 || l0 === 1 ? 0 : d / (1 - Math.abs(2 * l0 - 1));
  const target = relativeLuminance(r, g, b);
  const h = ((hue % 360) + 360) % 360;

  // Luminance rises monotonically with lightness at a fixed hue/saturation,
  // so bisection converges; 18 steps lands well inside one 8-bit step.
  let lo = 0;
  let hi = 1;
  let rgb = hslToRgb(h, sat, l0);
  for (let i = 0; i < 18; i++) {
    const mid = (lo + hi) / 2;
    rgb = hslToRgb(h, sat, mid);
    if (relativeLuminance(rgb[0], rgb[1], rgb[2]) < target) lo = mid;
    else hi = mid;
  }

  const toHex = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  const out = `#${toHex(rgb[0])}${toHex(rgb[1])}${toHex(rgb[2])}`;
  hueCache.set(key, out);
  return out;
};

const tintedChipStyle = (accent: string, baseStyle: CSSProperties): CSSProperties => ({
  ...baseStyle,
  backgroundColor: withAlpha(accent, 0.18),
  color: accent,
  border: `1px solid ${accent}`,
});

export const resolveStatusTone = (status: string): 'green' | 'red' | 'purple' | 'neutral' => {
  const key = mapStatusString(status);
  if (GREEN_STATUSES.has(key)) {
    return 'green';
  }
  if (RED_STATUSES.has(key)) {
    return 'red';
  }
  if (PURPLE_STATUSES.has(key)) {
    return 'purple';
  }

  // Only match multi-char status tokens — single letters like e/i are exact keys above.
  const tokens = key.split(/[\s_/-]+/).filter((token) => token.length > 1);
  if (tokens.some((token) => GREEN_STATUSES.has(token))) {
    return 'green';
  }
  if (tokens.some((token) => RED_STATUSES.has(token))) {
    return 'red';
  }
  if (tokens.some((token) => PURPLE_STATUSES.has(token))) {
    return 'purple';
  }

  return 'neutral';
};

/** Soft theme chip for shipment Type: Export (E) green, Import (I) purple. */
export const getShipmentTypeChipStyle = (isExport: boolean, themeColors: Record<string, string>): CSSProperties => {
  const baseStyle: CSSProperties = {
    borderRadius: 6,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  };
  const accent = isExport ? themeColors.green || themeColors.success : themeColors.purple || '#A855F7';
  return tintedChipStyle(accent, baseStyle);
};

/**
 * Mode-of-transport chip (SEA / AIR) — same tinted vocabulary as the shipment
 * Type chip beside it, so the two read as one pair rather than two systems.
 * SEA takes the theme's blue, AIR its info tone; both come from the palette so
 * all seven selectable themes stay consistent.
 */
export const getMotChipStyle = (
  mot: 'SEA' | 'AIR',
  themeColors: Record<string, string>
): CSSProperties => {
  // Both hues are re-cut from the theme's own blue, so each palette keeps its
  // chroma and lightness while sea and air stay a fixed 32 degrees apart —
  // indigo-blue for sea, cyan for air — in light and dark alike.
  const base = themeColors.blue || themeColors.primary || '#2563EB';
  const accent = withHue(base, mot === 'SEA' ? 222 : 190);
  return tintedChipStyle(accent, {
    borderRadius: 6,
    whiteSpace: 'nowrap',
  });
};

// Normalizes API status strings to a display key.
// Unknown statuses pass through as-is (lowercased).
export const mapStatusString = (status: string): string => {
  const aliasMap: Record<string, string> = {
    PEREKAMAN_DOKUMEN: 'perekaman dokumen',
    BILLING_SPTNP: 'billing sptnp',
    'BILLING SPTNP': 'billing sptnp',
  };

  const trimmed = status.trim();
  const upper = trimmed.toUpperCase();
  return aliasMap[upper] || trimmed.toLowerCase();
};

export const getThemeChipStyle = (
  themeColors: Record<string, string>,
  overrides: CSSProperties = {}
): CSSProperties => ({
  backgroundColor: themeColors.formFields,
  color: themeColors.text,
  border: `1px solid ${themeColors.border}`,
  borderRadius: 6,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  ...overrides,
});

/**
 * Dokumen State chips (FINAL / DRAFT) — soft tariff-badge style using theme tokens.
 * FINAL → accent teal; DRAFT → warning sand/amber. Avoids neon Mantine green/yellow.
 */
export const getDocumentStateChipStyle = (
  state: string | undefined | null,
  themeColors: Record<string, string>,
  overrides: CSSProperties = {}
): CSSProperties => {
  const isFinal = (state || 'DRAFT').toUpperCase() === 'FINAL';
  const base = isFinal
    ? themeColors.accent || themeColors.primary
    : themeColors.warning || themeColors.textSecondary;

  return {
    backgroundColor: withAlpha(base, 0.14),
    color: base,
    border: `1px solid ${withAlpha(base, 0.32)}`,
    borderRadius: 6,
    fontWeight: 500,
    cursor: 'pointer',
    ...overrides,
  };
};

export const getStatusChipStyle = (status: string, themeColors: Record<string, string>): CSSProperties => {
  const tone = resolveStatusTone(status);
  const baseStyle: CSSProperties = {
    borderRadius: 6,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  };

  if (tone === 'green') {
    return tintedChipStyle(themeColors.green || themeColors.success, baseStyle);
  }

  if (tone === 'red') {
    const accent = themeColors.red || themeColors.error;
    return {
      ...tintedChipStyle(accent, baseStyle),
      backgroundColor: themeColors.errorBg || withAlpha(accent, 0.18),
    };
  }

  if (tone === 'purple') {
    return tintedChipStyle(themeColors.purple || '#A855F7', baseStyle);
  }

  return {
    ...baseStyle,
    backgroundColor: themeColors.formFields,
    color: themeColors.text,
    border: `1px solid ${themeColors.border}`,
  };
};

export interface StatusChipProps {
  status: string;
}

export const StatusChip: React.FC<StatusChipProps> = ({ status }) => {
  const { colors: themeColors } = useTheme();
  const label = mapStatusString(status);

  return (
    <span
      className="inline-block px-1.5 py-0.5 text-xs font-medium capitalize text-center"
      title={label.toUpperCase()}
      style={getStatusChipStyle(status, themeColors)}
    >
      {label.toUpperCase()}
    </span>
  );
};

export default StatusChip;
